import React, { useEffect, useRef, useState, useMemo } from "react";
import { ReactComponent as CopyIcon } from "../../../assets/img/dashboard-svgs/copy.svg";
import { ReactComponent as CheckIcon } from "../../../assets/img/dashboard-svgs/checkmark.svg";
import { ReactComponent as TrashIcon } from "../../../assets/img/dashboard-svgs/ob-trash.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/img/dashboard-svgs/ob-download.svg";

const UploadSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="16 16 12 12 8 16" />
    <line x1="12" y1="12" x2="12" y2="21" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </svg>
);

const isFileType = (t) => ["image", "svg_image", "video", "file_upload"].includes(t);
const isValueType = (t) => ["color", "font_name", "text", "textarea"].includes(t);

const validateColorValue = (rawVal) => {
  if (!rawVal || typeof rawVal !== "string") return { valid: true, cleanValue: rawVal, error: "" };
  const trimmed = rawVal.trim();
  if (!trimmed) return { valid: true, cleanValue: "", error: "" };

  if (trimmed.startsWith("##") || (trimmed.match(/#/g) || []).length > 1) {
    return {
      valid: false,
      cleanValue: trimmed,
      error: "Invalid hex color format."
    };
  }

  let hex = trimmed;
  if (!hex.startsWith("#")) {
    if (/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$|^[0-9a-fA-F]{8}$/.test(hex)) {
      hex = "#" + hex;
    } else {
      return {
        valid: false,
        cleanValue: trimmed,
        error: "Invalid hex color format."
      };
    }
  }

  const isValid = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(hex);
  if (!isValid) {
    return {
      valid: false,
      cleanValue: trimmed,
      error: "Invalid hex color format."
    };
  }

  return { valid: true, cleanValue: hex, error: "" };
};

const resolvedValue = (key, formValues, fieldMeta) => {
  const draftValue = formValues[key];
  if (typeof draftValue !== "undefined" && draftValue !== null && draftValue !== "") return draftValue;
  const meta = fieldMeta[key];
  if (meta?.uploaded && typeof meta.uploaded === "string") return meta.uploaded;
  if (meta?.file_url && typeof meta.file_url === "string") return meta.file_url;
  return "";
};

const getAccept = (t) => {
  if (t === "image") return "image/*";
  if (t === "svg_image") return "image/svg+xml,image/*";
  if (t === "video") return "video/*";
  return "*/*";
};

const validateMediaDimensions = async (file, question) => {
  const inputType = question?.input_type;
  const constraints = question?.media_constraints || {};

  if (!file || (!inputType?.includes("image") && inputType !== "video")) {
    return { valid: true };
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result;
      if (!src) return resolve({ valid: true });

      if (inputType === "video" || inputType?.startsWith("video")) {
        const video = document.createElement("video");
        video.onloadedmetadata = () => {
          const errors = [];
          const width = video.videoWidth;
          const height = video.videoHeight;
          const aspectRatio = width / height;

          if (constraints.aspect_ratio) {
            const [aspectW, aspectH] = String(constraints.aspect_ratio).split(":").map(Number);
            const expectedRatio = aspectW / aspectH;
            const tolerance = 0.01;
            if (Math.abs(aspectRatio - expectedRatio) > tolerance) {
              errors.push(`Aspect ratio: ${constraints.aspect_ratio} (current: ${(aspectRatio).toFixed(2)}:1)`);
            }
          }

          resolve({ valid: errors.length === 0, errors: errors.length > 0 ? errors : undefined, dimensions: { width, height } });
        };
        video.onerror = () => resolve({ valid: true });
        video.src = src;
      } else {
        const img = new Image();
        img.onload = () => {
          const errors = [];
          const width = img.naturalWidth;
          const height = img.naturalHeight;
          const resolution = `${width}x${height}`;

          if (constraints.resolution) {
            const allowedResolutions = Array.isArray(constraints.resolution) ? constraints.resolution : [constraints.resolution];
            const matchesResolution = allowedResolutions.some((res) => String(res).toLowerCase() === resolution.toLowerCase());
            if (!matchesResolution) {
              errors.push(`Resolution must be ${allowedResolutions.join(", ")} (current: ${resolution})`);
            }
          }

          resolve({ valid: errors.length === 0, errors: errors.length > 0 ? errors : undefined, dimensions: { width, height } });
        };
        img.onerror = () => resolve({ valid: true });
        img.src = src;
      }
    };
    reader.onerror = () => resolve({ valid: true });
    reader.readAsDataURL(file);
  });
};

const parseOptions = (options) => {
  if (!options) return [];
  if (Array.isArray(options)) return options;
  if (typeof options === "string") {
    try {
      const parsed = JSON.parse(options);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return options.split(",").map((item) => item.trim()).filter(Boolean);
    }
  }
  if (typeof options === "object") {
    return Object.keys(options).map((key) => ({ value: key, label: options[key] }));
  }
  return [];
};

const BrandStudioSection = ({
  section,
  formValues = {},
  fieldMeta = {},
  updateField,
  handleUpload,
  requestDelete,
  savingByKey = {},
  savedByKey = {},
  errorByKey = {},
  isSuperAdmin = false,
  isFirstTime = false,
  focusedFieldKey = null,
}) => {
  const categories = useMemo(() => (section?.subsections || []).map((sub) => ({
    slug: sub.subsectionSlug,
    display_name: sub.subsectionName,
    assets: sub.questions || [],
  })), [section]);

  const [activeCategory, setActiveCategory] = useState(categories[0]?.slug || null);
  const [expanded, setExpanded] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);
  const fileInputRef = useRef({});

  useEffect(() => {
    if (!categories.length) return;
    setActiveCategory((prev) => (prev && categories.some((c) => c.slug === prev) ? prev : categories[0].slug));
  }, [section?.sectionSlug, categories.length]);

  useEffect(() => {
    if (focusedFieldKey) {
      const category = categories.find((c) =>
        c.assets.some((a) => a.key === focusedFieldKey)
      );
      if (category) {
        setActiveCategory(category.slug);
        setExpanded((prev) => ({ ...prev, [focusedFieldKey]: true }));
      }
    }
  }, [focusedFieldKey, categories]);

  const isAssetDone = (field) => {
    const key = field.key;
    const t = field.input_type;
    const meta = fieldMeta[key] || {};
    const value = resolvedValue(key, formValues, fieldMeta);
    if (isFileType(t)) return !!(meta.uploaded || meta.file_url);
    if (isValueType(t)) return !!String(value || "").trim();
    return !!(String(value || "").trim() || meta.uploaded || meta.file_url);
  };

  const handleCopy = (key, text) => {
    const cleanText = String(text || "").replace(/<[^>]*>/g, "");
    navigator.clipboard.writeText(cleanText);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const allAssets = categories.flatMap((c) => c.assets);
  const doneCount = allAssets.filter(isAssetDone).length;
  const requiredTotal = allAssets.filter((a) => {
    if (isFirstTime) {
      return a.first_time_required === 1 || a.first_time_required === true;
    }
    return !!a.is_required;
  }).length;
  const requiredDone = allAssets.filter((a) => {
    if (isFirstTime) {
      return (a.first_time_required === 1 || a.first_time_required === true) && isAssetDone(a);
    }
    return !!a.is_required && isAssetDone(a);
  }).length;

  const activeData = categories.find((c) => c.slug === activeCategory);
  const rows = activeData?.assets || [];

  if (!categories.length) {
    return <div className="gob-empty-state">No brand categories found.</div>;
  }

  return (
    <div>
      <div className="gob-breadcrumb">
        <span>Publisher Channel</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12 }}>
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span className="current">Brand Studio</span>
      </div>

      <div className="gob-page-head">
        <h1 className="gob-page-title">Brand <span className="gradient">Studio</span></h1>
        <p className="gob-page-sub">
          Manage all visual assets for your Publisher. Assets auto-fill into your chosen theme.
        </p>
      </div>

      <div className="gob-stats">
        <div className="stat-card"><div className="stat-label">Total assets</div><div className="stat-value">{allAssets.length}</div></div>
        <div className="stat-card"><div className="stat-label">Done</div><div className="stat-value success">{doneCount}</div></div>
        <div className="stat-card"><div className="stat-label">Required done</div><div className="stat-value warn">{requiredDone}/{requiredTotal}</div></div>
        <div className="stat-card"><div className="stat-label">Remaining</div><div className="stat-value">{allAssets.length - doneCount}</div></div>
      </div>

      <div className="brand-category-bar">
        {categories.map((c) => {
          const done = c.assets.filter(isAssetDone).length;
          const pill = done === c.assets.length && c.assets.length > 0 ? "complete" : done > 0 ? "partial" : "empty";
          return (
            <button key={c.slug} className={`brand-cat-pill${activeCategory === c.slug ? " active" : ""}`} onClick={() => setActiveCategory(c.slug)}>
              <span>{c.display_name}</span>
              <span className={`row-status ${pill}`}>{done}/{c.assets.length}</span>
            </button>
          );
        })}
      </div>

      <div className="brand-table">
        <div className="brand-table-head">{activeData?.display_name}</div>

        {rows.length === 0 && (
          <div style={{ padding: 16, color: "var(--gob-ink-3)", fontSize: 13 }}>No assets in this category.</div>
        )}

        {rows.map((field) => {
          const key = field.key;
          const inputType = field.input_type;
          const meta = fieldMeta[key] || {};
          const rawUrl = meta.uploaded || meta.file_url || "";
          const fileUrl = typeof rawUrl === "string" ? rawUrl : "";
          const hasUpload = !!rawUrl;
          const isFile = isFileType(inputType) || (hasUpload && !isValueType(inputType) && inputType !== "single_select");
          const isValue = isValueType(inputType);
          const isSingleSelect = inputType === "single_select";
          const fileName = meta.file_name || (fileUrl ? fileUrl.split("/").pop() : "");
          const mimeType = meta.mime_type || "";
          const textValue = resolvedValue(key, formValues, fieldMeta);
          const done = isAssetDone(field);
          const isOpen = !!expanded[key];
          const saving = !!savingByKey[key];
          const saved = !!savedByKey[key];
          const err = errorByKey[key] || "";
          const options = parseOptions(field.options);
          const isFontQuestion = /font/i.test(field.key || "") || /font/i.test(field.prompt || "");
          const hasOptions = options.length > 0;
          const isInlineSelect = isSingleSelect && hasOptions;
          const canExpand = !isInlineSelect;
          const hasNonEmptyValue = !!String(textValue || "").trim() || !!fileUrl;

          return (
            <div key={key} id={`q-row-${key}`} data-key={key} className={`brand-table-row${isOpen ? " open" : ""}`}>
              <div
                className="brand-row-head"
                onClick={() => { if (canExpand) setExpanded((prev) => ({ ...prev, [key]: !prev[key] })); }}
                style={{ cursor: canExpand ? "pointer" : "default" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flex: 1, minWidth: 0 }}>
                  <div className="brand-row-label">
                    <div className="brand-asset-name">
                      {field.prompt}
                      {!!(isFirstTime ? (field.first_time_required === 1 || field.first_time_required === true) : field.is_required) && (
                        <span className="gob-req-tag" style={{ marginLeft: 6 }}>Required</span>
                      )}
                    </div>
                    {field.tooltip && <div className="brand-asset-spec">{field.tooltip}</div>}
                  </div>

                  {hasNonEmptyValue && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: 12 }} onClick={(e) => e.stopPropagation()}>
                      {isValue && inputType !== "color" && (
                        <button
                          type="button"
                          onClick={() => handleCopy(key, textValue)}
                          className="gob-btn-icon"
                          title="Copy text"
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: copiedKey === key ? "var(--gob-success, #10b981)" : "var(--gob-ink-3, #9ca3af)", transition: "color 0.15s" }}
                        >
                          {copiedKey === key ? <CheckIcon style={{ width: 12, height: 12 }} /> : <CopyIcon style={{ width: 12, height: 12 }} />}
                        </button>
                      )}

                      {isFile && fileUrl && (
                        <a
                          href={fileUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gob-btn-icon"
                          title="Download file"
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "var(--gob-ink-3, #9ca3af)", transition: "color 0.15s" }}
                        >
                          <DownloadIcon style={{ width: 12, height: 12 }} />
                        </a>
                      )}

                      {requestDelete && !isSuperAdmin && (
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => requestDelete(key)}
                          className="gob-btn-icon"
                          title="Clear value"
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "#ef4444", opacity: saving ? 0.5 : 1, transition: "opacity 0.15s" }}
                        >
                          <TrashIcon style={{ width: 12, height: 12 }} />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {isInlineSelect && (
                  <select
                    className="brand-inline-select"
                    value={textValue}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => updateField(field, e.target.value)}
                    onBlur={(e) => updateField(field, e.target.value, true)}
                    disabled={isSuperAdmin}
                    style={{
                      fontFamily: isFontQuestion && textValue ? String(textValue) : "inherit",
                      maxWidth: "200px"
                    }}
                  >
                    <option value="">{isFontQuestion ? "Select font" : "Select option"}</option>
                    {options.map((opt) => {
                      const optVal = typeof opt === "object" ? opt.value ?? opt.label : opt;
                      const optLbl = typeof opt === "object" ? opt.label ?? opt.value : opt;
                      return (
                        <option key={String(optVal)} value={optVal} style={isFontQuestion && optVal ? { fontFamily: String(optVal) } : undefined}>
                          {optLbl}
                        </option>
                      );
                    })}
                  </select>
                )}

                <span className={`row-status ${done ? "complete" : "empty"}`}>{done ? "Done" : "Empty"}</span>
                {canExpand && (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    className="brand-row-chev"
                    style={{ transform: isOpen ? "rotate(180deg)" : "none" }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                )}
              </div>

              {isOpen && canExpand && (
                <div style={{ padding: "0 16px 16px" }}>
                  {isFile && field.media_constraints && (
                    <div style={{ marginBottom: 12, padding: 10, backgroundColor: "var(--gob-bg-soft)", borderRadius: 6, fontSize: 12, color: "var(--gob-ink-2)" }}>
                      {field.media_constraints.resolution && (
                        <div>Resolution: {Array.isArray(field.media_constraints.resolution) ? field.media_constraints.resolution.join(" or ") : field.media_constraints.resolution}</div>
                      )}
                      {field.media_constraints.aspect_ratio && (
                        <div>Aspect Ratio: {field.media_constraints.aspect_ratio}</div>
                      )}
                    </div>
                  )}
                  {isFile && (
                    <div className="brand-upload-zone" onClick={() => { if (!isSuperAdmin) fileInputRef.current[key]?.click(); }} style={isSuperAdmin ? { cursor: "default" } : undefined}>
                      {saving ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, color: "var(--gob-ink-3)", fontSize: 13 }}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="gob-spin" style={{ width: 24, height: 24 }}>
                            <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" />
                          </svg>
                          Uploading...
                        </div>
                      ) : fileUrl ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                          {((inputType === "image" || inputType === "svg_image") || mimeType.startsWith("image/")) && (
                            <img src={fileUrl} alt={field.prompt} style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 6, objectFit: "contain" }} />
                          )}
                          {(inputType === "video" || mimeType.startsWith("video/")) && (
                            <video src={fileUrl} style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 6 }} controls />
                          )}
                          {!((inputType === "image" || inputType === "svg_image") || mimeType.startsWith("image/") || inputType === "video" || mimeType.startsWith("video/")) && (
                            <div style={{ fontSize: 12, color: "var(--gob-ink-2)" }}>Uploaded file</div>
                          )}

                          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                            <span style={{ fontSize: 12, color: "var(--gob-success)", fontWeight: 600 }}>✓ Uploaded</span>
                            <a
                              href={fileUrl}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="gob-btn-icon"
                              title="Download file"
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "inline-flex", color: "var(--gob-ink-3, #9ca3af)", transition: "color 0.15s" }}
                            >
                              <DownloadIcon style={{ width: 12, height: 12 }} />
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, color: "var(--gob-ink-3)", fontSize: 13 }}>
                          {isSuperAdmin ? (
                            <span style={{ fontSize: 12, color: "var(--gob-ink-3)" }}>No file uploaded</span>
                          ) : (
                            <>
                              <span style={{ width: 32, height: 32 }}><UploadSvg /></span>
                              Click to upload
                              <span style={{ fontSize: 11 }}>{inputType === "svg_image" ? "SVG, PNG" : inputType === "video" ? "MP4, MOV" : "PNG, JPG, WebP"}</span>
                            </>
                          )}
                        </div>
                      )}
                      {!isSuperAdmin && (
                        <input
                          type="file"
                          accept={getAccept(inputType)}
                          style={{ display: "none" }}
                          ref={(el) => { fileInputRef.current[key] = el; }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (!file) return;
                            const validation = await validateMediaDimensions(file, field);
                            if (!validation.valid) {
                              setValidationErrors((prev) => ({ ...prev, [key]: validation.errors?.join("; ") || "Invalid media" }));
                              setTimeout(() => setValidationErrors((prev) => ({ ...prev, [key]: "" })), 4000);
                            } else {
                              setValidationErrors((prev) => ({ ...prev, [key]: "" }));
                              handleUpload(field, file);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  )}

                  {isValue && inputType === "color" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <input
                          type="color"
                          value={
                            /^#([0-9a-fA-F]{6})$/.test(textValue)
                              ? textValue
                              : /^#([0-9a-fA-F]{3})$/.test(textValue)
                              ? `#${textValue[1]}${textValue[1]}${textValue[2]}${textValue[2]}${textValue[3]}${textValue[3]}`
                              : "#ffffff"
                          }
                          onChange={(e) => {
                            setValidationErrors((prev) => ({ ...prev, [key]: "" }));
                            updateField(field, e.target.value, true);
                          }}
                          disabled={isSuperAdmin}
                          style={{ width: 48, height: 40, borderRadius: 6, border: "1px solid var(--gob-border)", cursor: isSuperAdmin ? "default" : "pointer", background: "none" }}
                        />
                        <input
                          className="gob-input"
                          type="text"
                          value={textValue}
                          onChange={(e) => {
                            setValidationErrors((prev) => ({ ...prev, [key]: "" }));
                            updateField(field, e.target.value, false, null, true);
                          }}
                          onBlur={(e) => {
                            const raw = e.target.value;
                            const res = validateColorValue(raw);
                            if (!res.valid) {
                              setValidationErrors((prev) => ({ ...prev, [key]: res.error }));
                            } else {
                              setValidationErrors((prev) => ({ ...prev, [key]: "" }));
                              updateField(field, res.cleanValue, true, null, false);
                            }
                          }}
                          placeholder="#000000"
                          disabled={isSuperAdmin}
                          style={{ flex: 1, borderColor: validationErrors[key] ? "#ef4444" : undefined }}
                        />
                      </div>
                    </div>
                  )}

                  {isSingleSelect && hasOptions && !isInlineFontSelect && (
                    <select
                      className="gob-input"
                      value={textValue}
                      onChange={(e) => updateField(field, e.target.value)}
                      onBlur={(e) => updateField(field, e.target.value, true)}
                      disabled={isSuperAdmin}
                      style={{ marginTop: 4, fontFamily: isFontQuestion && textValue ? String(textValue) : "inherit" }}
                    >
                      <option value="">Select an option</option>
                      {options.map((opt) => {
                        const optVal = typeof opt === "object" ? opt.value ?? opt.label : opt;
                        const optLbl = typeof opt === "object" ? opt.label ?? opt.value : opt;
                        return (
                          <option key={String(optVal)} value={optVal} style={isFontQuestion && optVal ? { fontFamily: String(optVal) } : undefined}>
                            {optLbl}
                          </option>
                        );
                      })}
                    </select>
                  )}

                  {isValue && inputType !== "color" && !isInlineFontSelect && !isSingleSelect && (
                    <input
                      className="gob-input"
                      type="text"
                      value={textValue}
                      onChange={(e) => updateField(field, e.target.value)}
                      onBlur={(e) => updateField(field, e.target.value, true)}
                      placeholder={isFontQuestion ? "Select a font" : "Enter value..."}
                      disabled={isSuperAdmin}
                      style={{ marginTop: 4 }}
                    />
                  )}

                  {!isFile && !isValue && !isSingleSelect && (
                    <textarea
                      className="gob-input"
                      rows={3}
                      value={textValue}
                      onChange={(e) => updateField(field, e.target.value)}
                      onBlur={(e) => updateField(field, e.target.value, true)}
                      placeholder="Enter value..."
                      disabled={isSuperAdmin}
                      style={{ marginTop: 4 }}
                    />
                  )}

                  {validationErrors[key] && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "var(--gob-error)" }}>
                      ⚠ {validationErrors[key]}
                    </div>
                  )}
                  {saving && <div className="q-help" style={{ marginTop: 6 }}>Saving...</div>}
                  {saved && <div className="q-help" style={{ marginTop: 6, color: "var(--gob-success)" }}>Saved ✓</div>}
                  {err && <div style={{ marginTop: 6, fontSize: 12, color: "var(--gob-error)" }}>{err}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BrandStudioSection;
